import {
    queryGraphQL,
} from 'common/util/query';
import {
    toQuery,
    nodeWithArgs,
    node,
    enumeration
} from 'common/util/queryHelper';
import lodash from 'lodash';
import { addApplicationMessage } from 'common/actions/applicationMessagesActions';
import { binderInstitutionIdSelector } from 'common/selectors';

export const LOAD_DOCUMENTS = 'LOAD_DOCUMENTS';
export const SET_FILE_STATE = 'SET_FILE_STATE';
export const REMOVE_FILES = 'REMOVE_FILES';
export const LOAD_DESCRIPTOR_SYSTEM_NAMES = 'LOAD_DESCRIPTOR_SYSTEM_NAMES';
const DEFAULT_UNFILED_DOCUMENT_LIMIT = 1000;
const DEFAULT_UNFILED_DOCUMENT_OFFSET = 0;

const documentShape = [
    'id',
    'name',
    'documentStatus',
    'conversionStatus',
    'uploadDate',
    'binderId',
    'institutionId',
    node('uploadUser', [
        'displayName',
        'emailAddress',
    ]),
    node('documentType', [
        'id',
        'displayName'
    ]),
    node('descriptors', [
        'id',
        'displayName',
        'values'
    ]),
    node('signatures', [
        'dateSigned'
    ])
];

const unfiledDocumentShape = [
    'id',
    'name',
    'documentStatus',
    'conversionStatus',
    'uploadDate',
    'binderId',
    node('uploadUser', [
        'displayName',
        'emailAddress',
    ]),
];

const duplicateDocumentShape = [
    'documentId',
    node('duplicates', [
        'documentId',
        'documentName',
        'documentTypeId',
    ])
];

export function loadDescriptorSystemNames() {
    return async (dispatch, getState) => {
        const storeState = getState();
        const institutionId = binderInstitutionIdSelector(storeState);
        if (institutionId) {
            const query = toQuery(nodeWithArgs('descriptors', {
                institutionId
            }, [
                'id',
                'name',
                'displayName',
                'systemName',
            ]));

            const response = await queryGraphQL(query);
            if (response.errors) {
                dispatch(addApplicationMessage('error', 'An error has occurred getting document metadata. Please try again later.'));
            } else {
                dispatch(setDescriptorSystemNamesState(response.data.descriptors));
            }

        }
        else {
            return;
        }

    };
}

function setDescriptorSystemNamesState(descriptors) {
    return {
        type: LOAD_DESCRIPTOR_SYSTEM_NAMES,
        payload: {
            descriptors,
        }
    };
}

export function loadDocuments(binderId, searchOptions, method = 'append') {
    return (dispatch, getState) => {
        const storeState = getState();
        const institutionId = binderInstitutionIdSelector(storeState);

        if (lodash.get(storeState, 'files.loadingDocuments')) {
            return;
        }
        dispatch(setFileState('loadingDocumentsError', false));
        dispatch(setFileState('loadingDocuments', true));
        searchOptions = lodash.cloneDeep(searchOptions);
        let queryName, countQueryName, queryShape;
        if (searchOptions.unfiledDocuments || searchOptions.folderId === 'unfiled') {
            queryName = 'unfiledDocuments';
            countQueryName = 'countUnfiledDocuments';
            searchOptions.limit = DEFAULT_UNFILED_DOCUMENT_LIMIT;
            searchOptions.offset = DEFAULT_UNFILED_DOCUMENT_OFFSET;
            queryShape = unfiledDocumentShape;
        } else {
            queryName = 'documents';
            countQueryName = 'countDocuments';
            queryShape = documentShape;
        }
        delete searchOptions.unfiledDocuments;

        if (lodash.isEmpty(searchOptions.folderId)) {
            delete searchOptions.folderId;
        }

        if (searchOptions.sort) {
            searchOptions.sort = lodash.map(searchOptions.sort, ({ field, direction }) => ({
                field,
                direction: enumeration(direction)
            }));
        }

        const queryOpts = {
            ...searchOptions,
            id: binderId,
            institutionId: institutionId
        };

        const query = nodeWithArgs(queryName, {
            query: queryOpts
        }, queryShape);

        const countQuery = nodeWithArgs(countQueryName, {
            query: lodash.omit(queryOpts, 'limit', 'offset')
        });

        return queryGraphQL(toQuery([query, countQuery])).then(results => {
            if (results.errors) {
                dispatch(setFileState('loadingDocumentsError', true));
                return Promise.reject(new Error('Failed to load documents. Please try again later.'));
            }
            return { documents: results.data[queryName], count: results.data[countQueryName] };
        }).then(({ documents, count }) => {
            if (searchOptions.folderId === 'unfiled' && institutionId && !lodash.isEmpty(documents)) {
                const documentIds = documents.map(document => document.id);
                const query = nodeWithArgs('getAllDuplicateDocuments', {
                    binderId,
                    documentIds,
                    institutionId,
                }, duplicateDocumentShape);

                queryGraphQL(toQuery(query)).then(results => {
                    if (results.errors) {
                        dispatch(setFileState('loadingDocumentsError', true));
                        return Promise.reject(new Error('Failed to retrieve duplicate documents from api-documents service. Please try again later.'));
                    }
                    return results.data.getAllDuplicateDocuments;
                }).then((duplicates) => {
                    dispatch(loadDocumentsAction({
                        documents,
                        count,
                        method,
                        folder: searchOptions.folderId,
                        query: searchOptions.searchString,
                        duplicates,
                    }));

                    dispatch(setFileState('loadingDocuments', false));
                });
            } else {
                dispatch(loadDocumentsAction({
                    documents,
                    count,
                    method,
                    folder: searchOptions.folderId,
                    query: searchOptions.searchString,
                    duplicates: [], //bypass duplicate check outside of unfiled documents page
                }));

                dispatch(setFileState('loadingDocuments', false));
            }
        }).catch(err => {
            dispatch(addApplicationMessage('error', 'An error has occurred when fetching documents. Please try again later.'));
            dispatch(setFileState('loadingDocumentsError', true));
            dispatch(setFileState('loadingDocuments', false));
        });
    };
}

export function loadDocumentsAction({ documents, count, method, folder, query, duplicates }) {
    return {
        type: LOAD_DOCUMENTS,
        payload: {
            documents,
            count,
            method,
            folder,
            query,
            duplicates,
        }
    };
}

export function setFileState(property, value) {
    return {
        type: SET_FILE_STATE,
        payload: {
            property,
            value
        }
    };
}

export function removeFiles() {
    return {
        type: REMOVE_FILES
    };
}
